import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import size from '~/assets/icons/size.svg';
import tireSize from '~/assets/icons/tire_size.svg';
import wheelCollapse from '~/assets/icons/wheel_collapse.svg';
import wheelDeflection from '~/assets/icons/wheel_deflection.svg';
import wheelSettingsIcon from '~/assets/icons/wheel_settings.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	IModalReplyDefault,
	InputAction,
	ModalButtonColor,
	ModalType,
	VehicleTuningCategory,
	VehicleWheelsSetting,
	VehicleWheelTireProfile,
	WalletType,
} from '~/shared';
import tireBig from '../../assets/images/tire_icon_big.svg';
import tireLow from '../../assets/images/tire_icon_low.svg';
import tireRegular from '../../assets/images/tire_icon_regular.svg';
import { useBuy } from '../../hooks';
import {
	setActiveStage,
	setWheelSettings,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { SectionHeading } from '../SectionHeading';
import {
	StyledButtonContainer,
	StyledButtonContent,
	StyledContainer,
	StyledResetButton,
	StyledSettingsContainer,
	StyledSettingsItemContainer,
	StyledSettingsItemTitleContainer,
	StyledWheelSettingsItem,
	StyledWheelSizeContainer,
} from './WheelSettingsTab.styled';

const WheelSettingsTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const ownedWheelSettings = useObservable(
		rage.vehicleTuningShop.getOwnedWheelsSettings(),
	);
	const wheelSettingsRanges = useObservable(
		rage.vehicleTuningShop.getWheelsSettingsRanges(),
	);
	const wheelSettings = useLayoutSelector(
		(state) => state.tuning.wheelSettings,
	);
	const wheelTireProfile = useObservable(
		rage.vehicleTuningShop.getWheelTireProfile(),
	);
	const tireProfileChangePrice = useObservable(
		rage.vehicleTuningShop.getTireProfileChangePrice(),
	);
	const shopItems = useObservable(rage.vehicleTuningShop.getShopItems());
	const { handleBuy } = useBuy();
	const isInitializedRef = useRef(false);

	const hasInstalledCustomWheel = useMemo(() => {
		return shopItems.some(
			(item) => item.variants && item.variants.length > 0 && item.isInstalled,
		);
	}, [shopItems]);

	const activeTireProfile = wheelTireProfile ?? VehicleWheelTireProfile.MEDIUM;

	const handleChange = useCallback(
		(field: VehicleWheelsSetting, value: number) => {
			dispatch(
				setWheelSettings({
					...wheelSettings,
					[field]: value,
				}),
			);
		},
		[wheelSettings],
	);

	const handleResetModal = useCallback(
		(name: string, fields: VehicleWheelsSetting[]) => {
			if (!ownedWheelSettings) return;

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: `Вы действительно хотите сбросить раздел "${name}"?`,
					acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				(result) => {
					if (result.isAccepted) {
						const updatedWheelSettings = fields.reduce(
							(acc, field) => {
								acc[field] = ownedWheelSettings[field];
								return acc;
							},
							{ ...wheelSettings },
						);

						dispatch(setWheelSettings(updatedWheelSettings));
					}
				},
			);
		},
		[wheelSettings, ownedWheelSettings],
	);

	const handleTireProfileClick = useCallback(
		async (profile: VehicleWheelTireProfile) => {
			if (profile === activeTireProfile) {
				return;
			}

			playSound(Sound.ClickButton);
			await rage.vehicleTuningShop.setTiresProfilePreview(profile);

			const price = tireProfileChangePrice ?? 0;
			rage.modal.showModal(
				{
					type: ModalType.Price,
					title: 'Подтверждение',
					text: `Вы действительно хотите приобрести профиль шин?`,
					price,
					wallet: WalletType.Cash,
					acceptButton: {
						text: 'Оплатить',
						color: ModalButtonColor.Success,
					},
					cancelButton: {
						text: 'Отменить',
						color: ModalButtonColor.Secondary,
					},
				},
				(result: IModalReplyDefault) => {
					if (result.isAccepted) {
						rage.vehicleTuningShop.buyWheelTireProfile(profile);
					} else {
						rage.vehicleTuningShop.resetTiresProfilePreview();
					}
				},
			);
		},
		[activeTireProfile, tireProfileChangePrice],
	);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage('category'));
	}, []);

	useEffect(() => {
		if (!ownedWheelSettings || !isInitializedRef.current) {
			return;
		}
		rage.vehicleTuningShop
			.setWheelsSettings(wheelSettings)
			.catch((err) => rage.notifications.showException(err));
	}, [wheelSettings, ownedWheelSettings]);

	useInputAction(InputAction.CommonMenuAccept, () =>
		handleBuy(WalletType.Cash),
	);

	useEffectOnce(() => {
		const seenTrackerKey = `${VehicleTuningCategory.WHEEL_SETTINGS}`;
		rage.vehicleTuningShop.markSeen(seenTrackerKey);
	});

	useEffect(() => {
		if (ownedWheelSettings) {
			dispatch(setWheelSettings(ownedWheelSettings));
			isInitializedRef.current = true;
		}
	}, [ownedWheelSettings]);

	if (!ownedWheelSettings || !wheelSettingsRanges) {
		return null;
	}

	return (
		<StyledContainer>
			<SectionHeading icon={wheelSettingsIcon} text={'Настройка колес'} />
			<ScrollContainer height={569} showMask style={{ marginTop: 20 }}>
				<StyledSettingsContainer>
					{hasInstalledCustomWheel && (
						<StyledSettingsItemContainer>
							<StyledWheelSizeContainer>
								<StyledWheelSettingsItem
									$active={activeTireProfile === VehicleWheelTireProfile.LOW}
									onClick={() =>
										handleTireProfileClick(VehicleWheelTireProfile.LOW)
									}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<img src={tireLow} />
									<div>
										<Text>Профиль</Text>
										<Text>Низкий</Text>
									</div>
								</StyledWheelSettingsItem>
								<StyledWheelSettingsItem
									$active={activeTireProfile === VehicleWheelTireProfile.MEDIUM}
									onClick={() =>
										handleTireProfileClick(VehicleWheelTireProfile.MEDIUM)
									}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<img src={tireRegular} />
									<div>
										<Text>Профиль</Text>
										<Text>Средний</Text>
									</div>
								</StyledWheelSettingsItem>
								<StyledWheelSettingsItem
									$active={activeTireProfile === VehicleWheelTireProfile.HIGH}
									onClick={() =>
										handleTireProfileClick(VehicleWheelTireProfile.HIGH)
									}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<img src={tireBig} />
									<div>
										<Text>Профиль</Text>
										<Text>Высокий</Text>
									</div>
								</StyledWheelSettingsItem>
							</StyledWheelSizeContainer>
						</StyledSettingsItemContainer>
					)}
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Развал колес'} icon={wheelCollapse} />
							<StyledResetButton
								onClick={() =>
									handleResetModal('Развал колес', [
										VehicleWheelsSetting.FRONT_CAMBER,
										VehicleWheelsSetting.REAR_CAMBER,
									])
								}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
							>
								Сбросить
							</StyledResetButton>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Передние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.FRONT_CAMBER]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.FRONT_CAMBER, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_CAMBER][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_CAMBER][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
								<PropertyContainerRow>
									<Text>Задние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.REAR_CAMBER]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.REAR_CAMBER, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_CAMBER][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_CAMBER][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Вылет колес'} icon={wheelDeflection} />
							<StyledResetButton
								onClick={() =>
									handleResetModal('Вылет колес', [
										VehicleWheelsSetting.FRONT_OFFSET,
										VehicleWheelsSetting.REAR_OFFSET,
									])
								}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
							>
								Сбросить
							</StyledResetButton>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Передние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.FRONT_OFFSET]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.FRONT_OFFSET, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_OFFSET][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.FRONT_OFFSET][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
								<PropertyContainerRow>
									<Text>Задние</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.REAR_OFFSET]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.REAR_OFFSET, value);
										}}
										min={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_OFFSET][0]
										}
										max={
											wheelSettingsRanges[VehicleWheelsSetting.REAR_OFFSET][1]
										}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Ширина колес'} icon={tireSize} />
							<StyledResetButton
								onClick={() =>
									handleResetModal('Ширину колес', [VehicleWheelsSetting.WIDTH])
								}
							>
								Сбросить
							</StyledResetButton>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Ширина</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.WIDTH]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.WIDTH, value);
										}}
										min={wheelSettingsRanges[VehicleWheelsSetting.WIDTH][0]}
										max={wheelSettingsRanges[VehicleWheelsSetting.WIDTH][1]}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
					<StyledSettingsItemContainer>
						<StyledSettingsItemTitleContainer>
							<SectionHeading text={'Размер колес'} icon={size} />
							<StyledResetButton
								onClick={() =>
									handleResetModal('Размер колес', [
										VehicleWheelsSetting.RADIUS,
									])
								}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
							>
								Сбросить
							</StyledResetButton>
						</StyledSettingsItemTitleContainer>
						{ownedWheelSettings && wheelSettingsRanges && (
							<PropertyContainer>
								<PropertyContainerRow>
									<Text>Размер</Text>
									<Slider
										value={wheelSettings[VehicleWheelsSetting.RADIUS]}
										onChange={(value) => {
											handleChange(VehicleWheelsSetting.RADIUS, value);
										}}
										min={wheelSettingsRanges[VehicleWheelsSetting.RADIUS][0]}
										max={wheelSettingsRanges[VehicleWheelsSetting.RADIUS][1]}
										step={0.001}
										valueInput
									/>
								</PropertyContainerRow>
							</PropertyContainer>
						)}
					</StyledSettingsItemContainer>
				</StyledSettingsContainer>
			</ScrollContainer>
			<StyledButtonContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={handleBack}
				>
					<StyledButtonContent>
						<img src={arrowLeft} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonContainer>
		</StyledContainer>
	);
};

export { WheelSettingsTab };
